/**
 * Video Service for AI Academy
 * Handles video URL fetching with caching
 */
import axios, { AxiosError } from 'axios';
import {  getConfig } from '../config/Constants.ts';

// ============================================================================
// Types
// ============================================================================

export interface VideoRequest {
  courseId: string;
  segmentId: string;
}

export interface TopicVideoRequest {
  courseId: string;
  topicId: string;
}

export interface VideoMetadata {
  size_bytes?: number;
  size_mb?: number;
  last_modified?: string;
  content_type?: string;
}

export interface VideoResponse {
  video_url: string;
  expires_at: string;
  metadata?: VideoMetadata;
}

// ============================================================================
// Video Service Class
// ============================================================================

class VideoService {
  private apiUrl: string;
  private urlCache: Map<string, { url: string; expiresAt: Date }>;

  constructor() {
    // Use your existing API URL from env
    this.apiUrl = getConfig().apiUrl || 'http://localhost:8002';
    this.urlCache = new Map();
  }

  /**
   * Generate cache key from identifiers
   */
  private getCacheKey(request: VideoRequest): string {
    return `${request.courseId}/${request.segmentId}`;
  }

  /**
   * Check if cached URL is still valid
   */
  private isCacheValid(cacheKey: string): boolean {
    const cached = this.urlCache.get(cacheKey);
    if (!cached) return false;

    // Check if URL has expired (with 5 minute buffer)
    const bufferMs = 5 * 60 * 1000;
    return cached.expiresAt.getTime() - bufferMs > Date.now();
  }

  /**
   * Get auth token from localStorage
   */
  private getAuthToken(): string | null {
    return localStorage.getItem('access_token');
  }

  /**
   * Get video URL with caching
   */
  async getVideoUrl(request: VideoRequest): Promise<VideoResponse> {
    const cacheKey = this.getCacheKey(request);

    // Check cache first
    if (this.isCacheValid(cacheKey)) {
      const cached = this.urlCache.get(cacheKey)!;
      return {
        video_url: cached.url,
        expires_at: cached.expiresAt.toISOString(),
      };
    }

    // Fetch new URL from backend
    try {
      const token = this.getAuthToken();

      const response = await axios.post<VideoResponse>(
        `${this.apiUrl}/videos/get-video-url`,
        {
          course_id: request.courseId,
          segment_id: request.segmentId,
        },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );

      // Cache the URL
      this.urlCache.set(cacheKey, {
        url: response.data.video_url,
        expiresAt: new Date(response.data.expires_at),
      });

      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error; // Never reaches here due to handleError throwing
    }
  }

  /**
   * Get topic video URL with caching
   */
  async getTopicVideoUrl(request: TopicVideoRequest): Promise<VideoResponse> {
    const cacheKey = `${request.courseId}/${request.topicId}`;

    // Check cache first
    if (this.isCacheValid(cacheKey)) {
      const cached = this.urlCache.get(cacheKey)!;
      return {
        video_url: cached.url,
        expires_at: cached.expiresAt.toISOString(),
      };
    }

    // Fetch new URL from backend
    try {
      const token = this.getAuthToken();

      const response = await axios.post<VideoResponse>(
        `${this.apiUrl}/videos/get-topic-video-url`,
        {
          course_id: request.courseId,
          topic_id: request.topicId,
        },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );

      // Cache the URL
      this.urlCache.set(cacheKey, {
        url: response.data.video_url,
        expiresAt: new Date(response.data.expires_at),
      });

      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error; // Never reaches here due to handleError throwing
    }
  }

  /**
   * Clear the URL cache
   */
  clearCache(): void {
    this.urlCache.clear();
  }

  /**
   * Handle API errors with user-friendly messages
   */
  private handleError(error: unknown): never {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<{ detail: string }>;

      if (axiosError.response?.status === 404) {
        throw new Error('Video not available for this segment yet');
      } else if (axiosError.response?.status === 403) {
        throw new Error('You must be enrolled in this course to access videos');
      } else if (axiosError.response?.status === 401) {
        throw new Error('Please log in to access course videos');
      } else if (axiosError.response?.data?.detail) {
        throw new Error(axiosError.response.data.detail);
      }
    }

    throw new Error('Failed to load video. Please try again later.');
  }
}

// ============================================================================
// Singleton Export
// ============================================================================

export const videoService = new VideoService();